// The look picker: skin tone, hair style, hair color and outfit, with the trainer previewed from four sides. One
// component for the trainer's creation step (the first-time tutorial) and for changing the look later (the Outfits
// tab). Changing the look is always free; outfits are only the ones the trainer owns. Art owns this file and the
// "Look picker" block in public/themes/codex.css.
//
//   const d = lookDraft();                     // what the trainer has now (or lookDraft({ outfit: 'wayfarer' }))
//   el.innerHTML = lookPicker(d);              // HTML; its buttons carry data-act="look-..."
//   if (lookPickerClick(d, button)) redraw();  // in the app's click handler: updates the draft
//   lookSave(send, d);                         // one { t: 'look' } message
//
// With { live: true } the app saves each click itself (the Outfits tab); without it no click leaves the draft.
import { SKIN_TONES, HAIR_STYLES, HAIR_COLORS, lookCode, lookOf, type Look } from '../shared/looks.ts';
import { OUTFITS, OUTFIT, outfitOf, ownsOutfit } from '../shared/outfits.ts';
import type { ClientMsg } from '../shared/protocol.ts';
import { S } from './state.ts';
import { spriteUrl } from './assets.ts';
import { trainerSprite } from './trainerart.ts';
import { esc } from './fmt.ts';

/** A look being picked, not yet saved. */
export interface LookDraft { look: Look; outfit: string }

export interface LookPickerOpts {
  /** Show the outfit row (default true). Off where outfit cards sit beside the picker. */
  outfits?: boolean;
  /** Show the Random button (default true). */
  random?: boolean;
  /** Saved on every click (the Outfits tab; client/outfits.ts handles it). Leave off to keep a draft and save it yourself. */
  live?: boolean;
}

/** A draft of what the signed-in trainer has now, with any field replaced. */
export function lookDraft(over: Partial<LookDraft> = {}): LookDraft {
  return { look: lookOf(S.trainer?.look), outfit: outfitOf(S.trainer?.outfit).id, ...over };
}

/** Whether the draft differs from what the trainer has now. */
export function lookChanged(d: LookDraft): boolean {
  return lookCode(d.look) !== lookCode(lookOf(S.trainer?.look)) || d.outfit !== outfitOf(S.trainer?.outfit).id;
}

/** Saves the draft (free, any number of times). */
export function lookSave(send: (m: ClientMsg) => void, d: LookDraft) {
  send({ t: 'look', look: lookCode(d.look), outfit: d.outfit });
}

/** The outfits a trainer can pick here: every one they own, free ones first. */
function wearable(): string[] {
  const t = S.trainer;
  return OUTFITS.filter((o) => (t ? ownsOutfit(t, o.id) : o.source.kind === 'default')).map((o) => o.id);
}

const DIRS = ['down', 'left', 'up', 'right'] as const;
/** Which side the big preview shows; a click on a small view swaps it in. Looks only, never saved. */
let view = 0;
const pressed = (on: boolean) => ` aria-pressed="${on}"${on ? ' class="on"' : ''}`;

/** The picker as HTML. */
export function lookPicker(d: LookDraft, opts: LookPickerOpts = {}): string {
  const id = trainerSprite(d.outfit, d.look);
  const fit = outfitOf(d.outfit);
  // A hood, hat or helmet hides every hair style but the long ones (art/rig3d/build-trainer.ts draws only those).
  const shows = (o: string, h: number) => h === 0 || trainerSprite(o, { ...d.look, hair: h }) !== trainerSprite(o, { ...d.look, hair: 0 });
  const bare = (o: string) => HAIR_STYLES.every((_, h) => shows(o, h));
  // So the styles are previewed bare-headed: on the outfit worn when it shows them all, else on a free one that does.
  const head = bare(d.outfit) ? d.outfit : OUTFITS.find((o) => o.source.kind === 'default' && bare(o.id))?.id ?? d.outfit;
  const skins = SKIN_TONES.map((s, i) => `<button type="button" data-act="look-skin" data-id="${i}"${pressed(d.look.skin === i)} title="${esc(s.name)}" aria-label="${esc(s.name)} skin"><i style="background:linear-gradient(135deg, ${s.ramp[4]}, ${s.ramp[3]} 45%, ${s.ramp[2]})"></i></button>`).join('');
  const styles = HAIR_STYLES.map((h, i) => {
    const face = spriteUrl(`${trainerSprite(head, { ...d.look, hair: i })}-down-0`);
    const hid = !shows(d.outfit, i);
    return `<button type="button" data-act="look-hair" data-id="${i}"${pressed(d.look.hair === i)}${hid ? ` title="Hidden under the ${esc(fit.name)}"` : ''}><span class="lk-face${hid ? ' hid' : ''}" style="background-image:url('${face}')"></span>${esc(h.name)}</button>`;
  }).join('');
  const colors = HAIR_COLORS.map((c, i) => `<button type="button" data-act="look-color" data-id="${i}"${pressed(d.look.color === i)} title="${esc(c.name)}" aria-label="${esc(c.name)} hair"><i style="background:linear-gradient(135deg, ${c.ramp[4]}, ${c.ramp[3]} 40%, ${c.ramp[1]})"></i></button>`).join('');
  const fits = opts.outfits === false ? '' : wearable().map((o) => `<button type="button" data-act="look-outfit" data-id="${o}"${pressed(d.outfit === o)} title="${esc(OUTFIT[o].name)}"><img src="${spriteUrl(`${trainerSprite(o, d.look)}-down-0`)}" alt="${esc(OUTFIT[o].name)}"></button>`).join('');
  return `<div class="lookpk"${opts.live ? ' data-live' : ''}>
    <div class="lk-prev"><img class="lk-big" src="${spriteUrl(`${id}-${DIRS[view]}-0`)}" alt="Your trainer">
      <div class="lk-dirs">${DIRS.map((dir, i) => (i === view ? '' : `<button type="button" data-act="look-view" data-id="${i}" title="Show larger" aria-label="Show the ${dir === 'down' ? 'front' : dir === 'up' ? 'back' : `${dir} side`} larger"><img src="${spriteUrl(`${id}-${dir}-0`)}" alt=""></button>`)).join('')}</div></div>
    <div class="lk-opts">
      <div class="lk-row"><span class="lk-lb">Skin</span><span class="lk-sw">${skins}</span></div>
      <div class="lk-row"><span class="lk-lb">Hair</span><span class="lk-hair">${styles}</span></div>
      <div class="lk-row"><span class="lk-lb">Hair color</span><span class="lk-sw">${colors}</span></div>
      ${fits ? `<div class="lk-row"><span class="lk-lb">Outfit</span><span class="lk-fits">${fits}</span><span class="lk-fitname">${esc(fit.name)}</span></div>` : ''}
      ${opts.random === false ? '' : `<div class="lk-row lk-end"><button type="button" class="btn small" data-act="look-random"${fits ? ' data-fits="1"' : ''}>Random</button></div>`}
    </div></div>`;
}

const pick = (n: number) => Math.floor(Math.random() * n);

/** Applies a click on the picker to the draft. True when it was the picker's (draw it again then). */
export function lookPickerClick(d: LookDraft, el: HTMLElement): boolean {
  const n = Number(el.dataset.id);
  switch (el.dataset.act) {
    case 'look-view': if (n >= 0 && n < DIRS.length) view = n; return true;
    case 'look-skin': d.look = { ...d.look, skin: n }; return true;
    case 'look-hair': d.look = { ...d.look, hair: n }; return true;
    case 'look-color': d.look = { ...d.look, color: n }; return true;
    case 'look-outfit': if (OUTFIT[el.dataset.id ?? '']) d.outfit = el.dataset.id!; return true;
    case 'look-random': {
      d.look = { skin: pick(SKIN_TONES.length), hair: pick(HAIR_STYLES.length), color: pick(HAIR_COLORS.length) };
      // With the outfit row shown, one of the free outfits too.
      const free = OUTFITS.filter((o) => o.source.kind === 'default').map((o) => o.id);
      if (el.dataset.fits) d.outfit = free[pick(free.length)];
      return true;
    }
    default: return false;
  }
}
